/**
 * Turn any thrown error / backend string into one short sentence a user can act on.
 *
 * Nothing unrecognised is ever passed through: raw exception text, JSON and stack
 * traces fall back to a generic sentence. The raw text is logged (``reportRawError``)
 * for support instead of shown.
 */

export const ERROR_KIND = Object.freeze({
  USAGE_LIMIT: "usage_limit",
  PROVIDER_LIMIT: "provider_limit",
  AUTH: "auth",
  SOURCE_UNAVAILABLE: "source_unavailable",
  TIMEOUT: "timeout",
  NETWORK: "network",
  GENERIC: "generic",
});

/** Next-step wording differs by where the error surfaced. */
const RETRY_HINT = { sync: "Try Sync now.", ai: "Try again.", default: "Try again." };

const SOURCE_CODES = new Set(["SOURCE_MAPPING_MISSING", "SOURCE_UNAVAILABLE", "RUN_INPUT_FAILED"]);

const PROVIDER_LIMIT_PATTERNS = [
  /credit balance is too low/i,
  /no credits remaining/i,
  /credit_balance_exhausted/i,
  /organization_spend_limit/i,
  /spend limit/i,
  /specified api usage limits/i,
  /api usage limits?/i,
  /insufficient_quota/i,
  /exceeded your current quota/i,
  /billing_error/i,
];

const SOURCE_PATTERNS = [
  /source table mapping is missing/i,
  /failed to fetch run input messages/i,
  /failed to read source content/i,
  /business (bigquery|gcs) destination/i,
  /failed to initialize (bigquery|gcs) client/i,
  /no such object|nosuchkey|gs:\/\//i,
];

// Mirrors connectorErrorLooksLikeAuth (businessDigestSetup) without its heavy imports.
const AUTH_PATTERNS = [
  /invalid_grant/i,
  /token (has been )?expired/i,
  /refresh token invalid/i,
  /re-?authenticate/i,
  /revoked/i,
  /unauthori[sz]ed/i,
  /invalid credentials/i,
  /insufficient authentication/i,
  /access_denied/i,
];

const TIMEOUT_PATTERNS = [/timed out/i, /timeout/i, /deadline exceeded/i, /etimedout/i];

const NETWORK_PATTERNS = [
  /failed to fetch/i,
  /networkerror/i,
  /network request failed/i,
  /load failed/i,
  /connectionpool/i,
  /connection (reset|refused|aborted)/i,
  /econn(reset|refused)/i,
];

const rawTextOf = (input) => {
  if (input == null) return "";
  if (typeof input === "string") return input.trim();
  if (typeof input === "object") {
    const direct = input.message ?? input.error ?? input.detail;
    if (typeof direct === "string") return direct.trim();
    try {
      return JSON.stringify(input);
    } catch (_) {
      return "";
    }
  }
  return String(input).trim();
};

const matchesAny = (text, patterns) => patterns.some((re) => re.test(text));

/**
 * Classify an error into an ``ERROR_KIND``.
 *
 * @example
 * classifyError({ code: "SOURCE_MAPPING_MISSING" }) // "source_unavailable"
 * classifyError("Error code: 400 - You have reached your specified API usage limits") // "provider_limit"
 */
export const classifyError = (input) => {
  const code = String((input && typeof input === "object" && input.code) || "").toUpperCase();
  const status = input && typeof input === "object" ? Number(input.status) || 0 : 0;
  const text = rawTextOf(input);

  if (code === "USAGE_LIMIT_REACHED" || status === 402) return ERROR_KIND.USAGE_LIMIT;
  if (SOURCE_CODES.has(code)) return ERROR_KIND.SOURCE_UNAVAILABLE;
  if (matchesAny(text, PROVIDER_LIMIT_PATTERNS)) return ERROR_KIND.PROVIDER_LIMIT;
  if (status === 401 || status === 403 || matchesAny(text, AUTH_PATTERNS)) {
    return ERROR_KIND.AUTH;
  }
  if (matchesAny(text, SOURCE_PATTERNS)) return ERROR_KIND.SOURCE_UNAVAILABLE;
  if (status === 408 || status === 504 || matchesAny(text, TIMEOUT_PATTERNS)) {
    return ERROR_KIND.TIMEOUT;
  }
  if (matchesAny(text, NETWORK_PATTERNS)) return ERROR_KIND.NETWORK;
  return ERROR_KIND.GENERIC;
};

/**
 * Describe an error for display.
 *
 * ``kind === "usage_limit"`` returns an empty message on purpose: the page-level
 * "out of AI credits" banner owns that case.
 *
 * @example
 * describeError(new Error("Failed to fetch run input messages: Source table mapping is missing"), { context: "ai" })
 * // { kind: "source_unavailable", message: "We couldn't load the original source for this task. Try again.", raw: "Failed to fetch…" }
 *
 * @example
 * describeError('{"error":"boom"}').message
 * // "Something went wrong. Try again — if it keeps happening, contact support."
 */
export const describeError = (input, { context = "default" } = {}) => {
  const kind = classifyError(input);
  const retry = RETRY_HINT[context] || RETRY_HINT.default;
  const raw = rawTextOf(input);
  let message;
  switch (kind) {
    case ERROR_KIND.USAGE_LIMIT:
      message = "";
      break;
    case ERROR_KIND.PROVIDER_LIMIT:
      message =
        "AI is paused for now because our AI provider's usage limit was reached. Your tasks are safe — try again later.";
      break;
    case ERROR_KIND.AUTH:
      message = "Your connection expired. Reconnect it in Data sources, then try again.";
      break;
    case ERROR_KIND.SOURCE_UNAVAILABLE:
      message = `We couldn't load the original source for this task. ${retry}`;
      break;
    case ERROR_KIND.TIMEOUT:
      message = `That took too long to respond. ${retry}`;
      break;
    case ERROR_KIND.NETWORK:
      message = `Couldn't reach the server. Check your connection. ${retry}`;
      break;
    default:
      message = "Something went wrong. Try again — if it keeps happening, contact support.";
  }
  return { kind, message, raw };
};

/**
 * Message-only helper that logs the raw text for support.
 *
 * @example
 * friendlyErrorMessage(err, { context: "sync" }) // "That took too long to respond. Try Sync now."
 */
export const friendlyErrorMessage = (input, options = {}) => {
  const described = describeError(input, options);
  if (described.message && described.raw && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(`[error:${described.kind}]`, described.raw);
  }
  return described.message;
};

/**
 * Friendly text for a server-authored ``outcome_detail`` (AI run result).
 *
 * Known failure kinds (provider spend cap, expired auth, ...) are replaced with the
 * mapped sentence; other text is the server's own wording and is kept as-is.
 *
 * @example
 * friendlyOutcomeDetail("Error code: 400 - You have reached your specified API usage limits")
 * // "AI is paused for now because our AI provider's usage limit was reached. ..."
 * friendlyOutcomeDetail("Not supported yet.") // "Not supported yet."
 */
export const friendlyOutcomeDetail = (detail) => {
  const text = String(detail || "").trim();
  if (!text) return "";
  const described = describeError(text, { context: "ai" });
  if (described.kind === ERROR_KIND.GENERIC || !described.message) return text;
  // eslint-disable-next-line no-console
  console.warn(`[error:${described.kind}]`, text);
  return described.message;
};
